import DOMPurify from 'dompurify';
import { getProgramArtists } from '../domain/program.ts';
import { parseRedirectRules } from '../../shared/redirects.mjs';
import type { RedirectRule } from '../../shared/redirects.mjs';
import type {
  Artist,
  DecoratedScheduleEntry,
  FAQAccordionData,
  FAQsData,
  FestivalData,
  NewsData,
  ProgramData,
  ScheduleData,
  SponsorsData,
  TicketsData,
} from './types.ts';

// Configurable via Vite env. For local dev points at /data/; for staging
// override to the Azure Blob URL via .env.staging; for production override
// to your CDN URL via .env.production. See .env.example.
const DATA_BASE: string = (import.meta.env.VITE_DATA_BASE || '/data/').replace(/\/?$/, '/');

// Strip inline styles + classes from CMS rich-text so it adopts our
// typography. We keep semantic tags + anchor hrefs, drop the rest.
const ALLOWED_TAGS: string[] = [
  'p','br','strong','b','em','i','u',
  'ul','ol','li','a','span','div',
  'h1','h2','h3','h4','h5','font',
];

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    // Allow links to open in a new tab. rel is kept so we can pair
    // target="_blank" with noopener/noreferrer to avoid tabnabbing.
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
}

async function fetchJSON(name: string): Promise<unknown> {
  const res = await fetch(DATA_BASE + name, { credentials: 'omit' });
  if (!res.ok) throw new Error(`Failed to load ${name} (HTTP ${res.status})`);
  return res.json() as Promise<unknown>;
}

export async function loadAll(): Promise<FestivalData> {
  const [program, schedule, news, sponsors, tickets, faqs, faqAccordion] =
    await Promise.all([
      fetchJSON('program.json') as Promise<ProgramData>,
      fetchJSON('schedule.json') as Promise<ScheduleData>,
      fetchJSON('news.json') as Promise<NewsData>,
      fetchJSON('sponsors.json') as Promise<SponsorsData>,
      fetchJSON('tickets.json') as Promise<TicketsData>,
      fetchJSON('faqs.json') as Promise<FAQsData>,
      (fetchJSON('faq.json') as Promise<FAQAccordionData>).catch((): FAQAccordionData => ({ items: [] })),
    ]);

  // Build artist lookup. A block a consumer reads unconditionally must never fail the whole
  // load: `artists` (and `entries`) are read through a tolerant accessor, so a program.json
  // without artists — the new year right after the festival — renders as empty lists.
  const artists = getProgramArtists(program);
  const artistById: Record<string, Artist> = {};
  artists.forEach(a => { artistById[a.id] = a; });

  // Decorate schedule with artist info
  const scheduleEntries: DecoratedScheduleEntry[] = (schedule.entries ?? []).map(e => ({
    ...e,
    artist: artistById[e.artistId],
  }));

  // Pre-sanitize artist + news html so consumers don't have to remember
  artists.forEach(a => {
    (a as { bioHtml?: string }).bioHtml = sanitizeHtml(a.announcementText);
  });
  news.items.forEach(n => {
    const mutable = n as { bodyHtml?: string; hasImage?: boolean };
    mutable.bodyHtml = sanitizeHtml(n.htmlContent);
    mutable.hasImage = !!n.imageUrl;
  });

  return {
    program,
    schedule,
    scheduleEntries,
    news,
    sponsors,
    tickets,
    faqs,
    faqAccordion,
    artistById,
  };
}

export const DATA_BASE_URL: string = DATA_BASE;

/**
 * Torsdag's (community-day) own schedule feed (CMS-061) — entries that never appear in the
 * regular schedule.json. Separate from loadAll() since only a schedule section placed on
 * /torsdag needs it. Soft-fails to an empty schedule (e.g. before FullProgramPublishUtc, when
 * the blob doesn't exist yet) rather than breaking the page.
 */
export async function loadThursdaySchedule(): Promise<ScheduleData> {
  try {
    return await fetchJSON('thursday.json') as ScheduleData;
  } catch {
    return { entries: [] };
  }
}

/**
 * Old-site URL redirects (see the "Redirect nuværende URL'er" task), published to the CDN like the
 * other feeds. The matching itself lives in `shared/redirects.mjs`, which the Node server imports
 * too, so a rule resolves the same way whether the server answers it with a real 301 or the app
 * follows it during in-app navigation. Re-exported here because the app has always reached for
 * these through data.ts.
 */
export { matchRedirect, matchRedirectRule } from '../../shared/redirects.mjs';
export type { RedirectRule, MatchedRedirect } from '../../shared/redirects.mjs';

/**
 * Loads the published rules; soft-fails to an empty list (e.g. before redirects.json is first
 * published) so the catch-all route just falls through to the 404 rather than breaking.
 */
export async function loadRedirects(): Promise<RedirectRule[]> {
  try {
    return parseRedirectRules(await fetchJSON('redirects.json'));
  } catch {
    return [];
  }
}

/**
 * Ung i Midten's (youth-day) own schedule feed (CMS-062) — entries that never appear in the
 * regular schedule.json. Mirrors {@link loadThursdaySchedule}: soft-fails to an empty schedule
 * (e.g. before FullProgramPublishUtc, when the blob doesn't exist yet) rather than breaking the page.
 */
export async function loadYouthDaySchedule(): Promise<ScheduleData> {
  try {
    return await fetchJSON('youthday.json') as ScheduleData;
  } catch {
    return { entries: [] };
  }
}
